import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://nomokratia.ai';

const AdminDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [actionMsg, setActionMsg] = useState('');
  const LIMIT = 20;

  const token = localStorage.getItem('nomokratia_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Stats error:', e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/admin/users?skip=${page * LIMIT}&limit=${LIMIT}`;
      if (filterPlan) url += `&plan=${filterPlan}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setTotalUsers(data.total);
      }
    } catch (e) { console.error('Users error:', e); }
    setLoading(false);
  }, [page, filterPlan, search]);

  useEffect(() => { fetchStats(); fetchUsers(); }, [fetchStats, fetchUsers]);

  const changePlan = async (userId, plan) => {
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/plan`, {
        method: 'PUT', headers, body: JSON.stringify({ plan })
      });
      if (res.ok) { setActionMsg('Plan ενημερώθηκε'); fetchUsers(); fetchStats(); }
    } catch (e) { setActionMsg('Σφάλμα'); }
    setTimeout(() => setActionMsg(''), 3000);
  };

  const suspendUser = async (userId) => {
    if (!window.confirm('Αποκλεισμός χρήστη;')) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, { method: 'DELETE', headers });
      if (res.ok) { setActionMsg('Χρήστης αποκλείστηκε'); fetchUsers(); fetchStats(); setSelectedUser(null); }
    } catch (e) { setActionMsg('Σφάλμα'); }
    setTimeout(() => setActionMsg(''), 3000);
  };

  const extendTrial = async (userId, days) => {
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/extend-trial`, {
        method: 'POST', headers, body: JSON.stringify({ days })
      });
      if (res.ok) { setActionMsg(`Trial +${days} ημέρες`); fetchUsers(); }
    } catch (e) { setActionMsg('Σφάλμα'); }
    setTimeout(() => setActionMsg(''), 3000);
  };

  const planLabel = (p) => ({ free_trial: 'Δοκιμαστικό', starter: 'Επαγγελματικό', professional: 'Γραφείο', suspended: 'Αποκλεισμένος' }[p] || p);
  const planColor = (p) => ({ free_trial: '#3B82F6', starter: '#059669', professional: '#7C3AED', suspended: '#DC2626' }[p] || '#64748B');

  // ─── Styles ────────────────────────────────────────────────────────
  const overlay = { position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(15,23,42,0.6)',backdropFilter:'blur(4px)',display:'flex',alignItems:'stretch',justifyContent:'center',zIndex:10000 };
  const container = { background:'#F8FAFC',width:'100%',maxWidth:1200,margin:'20px auto',borderRadius:16,overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.2)' };
  const header = { background:'#1E293B',padding:'20px 28px',display:'flex',alignItems:'center',justifyContent:'space-between' };
  const statCard = (bg) => ({ background:bg||'#FFF',borderRadius:12,padding:'16px 20px',flex:1,minWidth:140 });
  const badge = (color) => ({ display:'inline-block',padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,background:color+'18',color:color,whiteSpace:'nowrap' });
  const btn = (bg,clr) => ({ padding:'6px 14px',borderRadius:8,border:'none',background:bg,color:clr||'#FFF',fontSize:12,fontWeight:600,cursor:'pointer' });
  const inp = { width:'100%',padding:'10px 14px',borderRadius:10,border:'1px solid #E2E8F0',fontSize:14,outline:'none',boxSizing:'border-box' };

  return (
    <div style={overlay}>
      <div style={container}>

        {/* Header */}
        <div style={header}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:24}}>&#9881;&#65039;</span>
            <div>
              <h2 style={{margin:0,color:'#FFF',fontSize:18,fontWeight:800}}>Admin Dashboard</h2>
              <p style={{margin:0,color:'#94A3B8',fontSize:12}}>ΝΟΜΟΚΡΑΤΙΑ — Διαχείριση Χρηστών</p>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            {actionMsg && <span style={{color:'#4ADE80',fontSize:13,fontWeight:600}}>{actionMsg}</span>}
            <button onClick={onClose} style={{...btn('#334155','#FFF'),padding:'8px 16px'}}>&#10005; Κλείσιμο</button>
          </div>
        </div>

        <div style={{flex:1,overflowY:'auto',padding:24}}>

          {/* Stats Cards */}
          {stats && (
            <div style={{display:'flex',gap:12,marginBottom:24,flexWrap:'wrap'}}>
              <div style={statCard('#EFF6FF')}>
                <div style={{fontSize:11,color:'#3B82F6',fontWeight:700,marginBottom:4}}>ΧΡΗΣΤΕΣ</div>
                <div style={{fontSize:28,fontWeight:800,color:'#1E293B'}}>{stats.users.total}</div>
                <div style={{fontSize:11,color:'#64748B'}}>+{stats.users.new_today} σήμερα · +{stats.users.new_7d} εβδομάδα</div>
              </div>
              <div style={statCard('#F0FDF4')}>
                <div style={{fontSize:11,color:'#059669',fontWeight:700,marginBottom:4}}>ΕΝΕΡΓΟΙ (7d)</div>
                <div style={{fontSize:28,fontWeight:800,color:'#1E293B'}}>{stats.users.active_7d}</div>
                <div style={{fontSize:11,color:'#64748B'}}>{stats.users.verified} verified · {stats.users.with_2fa} 2FA</div>
              </div>
              <div style={statCard('#FFF7ED')}>
                <div style={{fontSize:11,color:'#EA580C',fontWeight:700,marginBottom:4}}>AI ΑΝΑΖΗΤΗΣΕΙΣ</div>
                <div style={{fontSize:28,fontWeight:800,color:'#1E293B'}}>{stats.searches.total}</div>
                <div style={{fontSize:11,color:'#64748B'}}>{stats.searches.today} σήμερα</div>
              </div>
              <div style={statCard('#FAF5FF')}>
                <div style={{fontSize:11,color:'#7C3AED',fontWeight:700,marginBottom:4}}>ΠΛΑΝΑ</div>
                <div style={{fontSize:13,color:'#1E293B',lineHeight:1.8}}>
                  {Object.entries(stats.plans || {}).map(([k,v]) => (
                    <div key={k}><span style={badge(planColor(k))}>{planLabel(k)}: {v}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{display:'flex',gap:12,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
            <input
              type="text"
              placeholder="&#128269; Αναζήτηση (email, όνομα, ΑΦΜ...)"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{...inp,maxWidth:320}}
            />
            <select value={filterPlan} onChange={e => { setFilterPlan(e.target.value); setPage(0); }}
              style={{...inp,maxWidth:180,cursor:'pointer'}}>
              <option value="">Όλα τα πλάνα</option>
              <option value="free_trial">Δοκιμαστικό</option>
              <option value="starter">Επαγγελματικό</option>
              <option value="professional">Γραφείο</option>
              <option value="suspended">Αποκλεισμένοι</option>
            </select>
            <span style={{fontSize:13,color:'#64748B'}}>{totalUsers} χρήστες</span>
          </div>

          {/* Users Table */}
          <div style={{background:'#FFF',borderRadius:12,border:'1px solid #E2E8F0',overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{background:'#F8FAFC',borderBottom:'2px solid #E2E8F0'}}>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Χρήστης</th>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Email</th>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Πλάνο</th>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Status</th>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Εγγραφή</th>
                  <th style={{padding:'10px 14px',textAlign:'left',color:'#64748B',fontWeight:600}}>Τελ. Σύνδεση</th>
                  <th style={{padding:'10px 14px',textAlign:'center',color:'#64748B',fontWeight:600}}>Αναζητήσεις</th>
                  <th style={{padding:'10px 14px',textAlign:'center',color:'#64748B',fontWeight:600}}>Κόστος API</th>
                  <th style={{padding:'10px 14px',textAlign:'center',color:'#64748B',fontWeight:600}}>Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{padding:40,textAlign:'center',color:'#94A3B8'}}>Φόρτωση...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={9} style={{padding:40,textAlign:'center',color:'#94A3B8'}}>Δεν βρέθηκαν χρήστες</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} style={{borderBottom:'1px solid #F1F5F9',cursor:'pointer',transition:'background 0.15s'}}
                    onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)}
                  >
                    <td style={{padding:'10px 14px'}}>
                      <div style={{fontWeight:600,color:'#1E293B'}}>{u.first_name} {u.last_name}</div>
                      {u.firm_name && <div style={{fontSize:11,color:'#94A3B8'}}>{u.firm_name}</div>}
                    </td>
                    <td style={{padding:'10px 14px',color:'#475569'}}>{u.email}</td>
                    <td style={{padding:'10px 14px'}}><span style={badge(planColor(u.plan))}>{planLabel(u.plan)}</span></td>
                    <td style={{padding:'10px 14px'}}>
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        {u.email_verified && <span style={badge('#059669')}>&#9989; Email</span>}
                        {!u.email_verified && <span style={badge('#DC2626')}>&#10060; Email</span>}
                        {u.totp_enabled && <span style={badge('#3B82F6')}>&#128274; 2FA</span>}
                      </div>
                    </td>
                    <td style={{padding:'10px 14px',color:'#64748B',fontSize:12}}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('el-GR') : '-'}
                    </td>
                    <td style={{padding:'10px 14px',color:'#64748B',fontSize:12}}>
                      {u.last_login && u.last_login !== 'None' && u.last_login !== '' ?
                        <span style={{color:'#2f855a',fontWeight:500}}>{new Date(u.last_login).toLocaleString('el-GR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</span>
                        : <span style={{color:'#a0aec0'}}>—</span>}
                    </td>
                    <td style={{padding:'10px 14px',textAlign:'center',fontWeight:600,color:u.search_count>0?'#2b6cb0':'#a0aec0'}}>
                      {u.search_count || 0}
                    </td>
                    <td style={{padding:'10px 14px',textAlign:'center',fontWeight:600,color:u.token_cost_eur>0?'#c05621':'#a0aec0'}}>
                      {u.token_cost_eur > 0 ? '€'+Number(u.token_cost_eur).toFixed(4) : '€0'}
                    </td>
                    <td style={{padding:'10px 14px',textAlign:'center'}}>
                      <div style={{display:'flex',gap:6,justifyContent:'center',flexWrap:'wrap'}} onClick={e => e.stopPropagation()}>
                        <select
                          value={u.plan}
                          onChange={e => changePlan(u.id, e.target.value)}
                          style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E2E8F0',fontSize:11,cursor:'pointer'}}
                        >
                          <option value="free_trial">Δοκιμαστικό</option>
                          <option value="starter">Επαγγελματικό</option>
                          <option value="professional">Γραφείο</option>
                          <option value="suspended">Αποκλεισμός</option>
                        </select>
                        {u.plan === 'free_trial' && (
                          <button onClick={() => extendTrial(u.id, 14)} style={btn('#3B82F6')} title="Παράταση Trial +14 ημέρες">+14d</button>
                        )}
                        <button onClick={() => suspendUser(u.id)} style={btn('#FEE2E2','#DC2626')} title="Αποκλεισμός">&#128683;</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Expanded User Detail */}
          {selectedUser && (
            <div style={{background:'#FFF',borderRadius:12,border:'1px solid #E2E8F0',padding:20,marginTop:12}}>
              <h3 style={{margin:'0 0 12px',fontSize:15,fontWeight:700,color:'#1E293B'}}>
                &#128100; {selectedUser.first_name} {selectedUser.last_name}
              </h3>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,fontSize:13}}>
                <div><span style={{color:'#94A3B8'}}>Email:</span> {selectedUser.email}</div>
                <div><span style={{color:'#94A3B8'}}>Τηλ:</span> {selectedUser.phone || '-'}</div>
                <div><span style={{color:'#94A3B8'}}>ΑΦΜ:</span> {selectedUser.vat_number || '-'}</div>
                <div><span style={{color:'#94A3B8'}}>Γραφείο:</span> {selectedUser.firm_name || '-'}</div>
                <div><span style={{color:'#94A3B8'}}>Δ.Σ.:</span> {selectedUser.bar_association || '-'}</div>
                <div><span style={{color:'#94A3B8'}}>Last Login:</span> {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString('el-GR') : 'Ποτέ'}</div>
                <div style={{gridColumn:'1/-1'}}>
                  <span style={{color:'#94A3B8'}}>Ειδικεύσεις:</span>{' '}
                  {(selectedUser.legal_areas || []).join(', ') || '-'}
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalUsers > LIMIT && (
            <div style={{display:'flex',gap:8,justifyContent:'center',marginTop:16}}>
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                style={{...btn(page === 0 ? '#E2E8F0' : '#1E293B', page === 0 ? '#94A3B8' : '#FFF')}}>
                &#8592; Προηγ.
              </button>
              <span style={{padding:'6px 14px',fontSize:13,color:'#64748B'}}>
                Σελ. {page + 1} / {Math.ceil(totalUsers / LIMIT)}
              </span>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * LIMIT >= totalUsers}
                style={{...btn((page + 1) * LIMIT >= totalUsers ? '#E2E8F0' : '#1E293B')}}>
                Επόμ. &#8594;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
